import { PermissionCode, ProductCode } from '@gb-ticket/shared-types';
import {
  BookOpenIcon,
  ContactIcon,
  MessageSquareIcon,
  NewspaperIcon,
  UsersIcon,
} from 'lucide-react';

import { KiMark } from '@/components/ki-mark';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { ProductSideNav } from '@/platform/components/product-side-nav';
import { registerProductNav } from '@/platform/lib/product-nav';
import { IntranetChannelsTab } from '@/products/intranet/components/intranet-channels-tab';
import { useIntranetMenuTiles } from '@/products/intranet/components/intranet-menu-extras';
import {
  useIntranetAiAgentsMine,
  useIntranetAssistant,
} from '@/products/intranet/features/intranet/use-intranet-ai';
import {
  INTRANET_AI_PATH,
  INTRANET_CHAT_PATH,
  INTRANET_COLLEAGUES_PATH,
  INTRANET_CONTACTS_PATH,
  INTRANET_EDITORIAL_PATH,
  INTRANET_LIBRARY_PATH,
  INTRANET_ROOT,
} from '@/products/intranet/intranet-paths';

const iconClass = 'size-4';

function IntranetNav() {
  const me = useMe();
  const canWrite = hasPermission(me.data, PermissionCode.INTRANET_CONTENT_WRITE);
  const canUseAi = hasPermission(me.data, PermissionCode.INTRANET_AI_USE);
  const assistant = useIntranetAssistant();
  const assistantOn = assistant.data?.intranetAssistant === true;
  const mine = useIntranetAiAgentsMine(canUseAi && assistantOn);
  const hasAgents = (mine.data?.items.length ?? 0) > 0;
  const showAiTab = canUseAi && assistantOn && hasAgents;

  return (
    <ProductSideNav
      ariaLabel="Intranet"
      items={[
        {
          to: INTRANET_ROOT,
          label: 'Start',
          end: true,
          icon: <NewspaperIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
        },
        {
          to: INTRANET_LIBRARY_PATH,
          label: 'Wissensdatenbank',
          icon: <BookOpenIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
        },
        {
          to: INTRANET_COLLEAGUES_PATH,
          label: 'Kollegen',
          icon: <UsersIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
        },
        {
          to: INTRANET_CHAT_PATH,
          label: 'Chat',
          icon: <MessageSquareIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
        },
        {
          to: INTRANET_CONTACTS_PATH,
          label: 'Ansprechpartner',
          icon: <ContactIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
        },
        ...(showAiTab
          ? [{ to: INTRANET_AI_PATH, label: 'KI', icon: <KiMark className="size-3.5" /> }]
          : []),
        ...(canWrite ? [{ to: INTRANET_EDITORIAL_PATH, label: 'Redaktion' }] : []),
      ]}
    />
  );
}

registerProductNav({
  code: ProductCode.INTRANET,
  Nav: IntranetNav,
  useExtraTiles: useIntranetMenuTiles,
  menuTiles: [
    { id: 'intranet-start', label: 'Start', to: INTRANET_ROOT },
    { id: 'intranet-library', label: 'Wissensdatenbank', to: INTRANET_LIBRARY_PATH },
    { id: 'intranet-colleagues', label: 'Kollegen', to: INTRANET_COLLEAGUES_PATH },
    { id: 'intranet-chat', label: 'Chat', to: INTRANET_CHAT_PATH },
    { id: 'intranet-contacts', label: 'Ansprechpartner', to: INTRANET_CONTACTS_PATH },
    {
      id: 'intranet-ai',
      label: 'KI',
      to: INTRANET_AI_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.INTRANET_AI_USE),
    },
    {
      id: 'intranet-editorial',
      label: 'Redaktion',
      to: INTRANET_EDITORIAL_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.INTRANET_CONTENT_WRITE),
    },
  ],
  mobileSlots: [
    {
      kind: 'action',
      id: 'intranet-channels',
      label: 'Channels',
      Component: IntranetChannelsTab,
    },
    {
      kind: 'link',
      id: 'intranet-chats',
      label: 'Chats',
      to: INTRANET_CHAT_PATH,
    },
  ],
});
